import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import logger from '../../utils/logger';
import moment from 'moment';
import { formatCurrency } from '../Help/helper';
import BreadCrumb from '../BreadCrumb';
import { ShoppingCartIcon, MagnifyingGlassIcon, FunnelIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const pages = [
  { name: 'Procurement', to: '/procurement', component: Link, current: false },
  { name: 'Purchase Orders', to: '/procurement/purchase-orders', component: Link, current: true },
];

const PurchaseOrderList = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrders, searchTerm, statusFilter]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'purchaseOrders'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const orders = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPurchaseOrders(orders);
    } catch (error) {
      logger.error('Error fetching purchase orders:', error);
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...purchaseOrders];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (po) =>
          po.poNumber?.toLowerCase().includes(searchLower) ||
          po.supplier?.toLowerCase().includes(searchLower) ||
          po.procurementNumber?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter((po) => po.status === statusFilter);
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  };

  const exportToCSV = () => {
    const headers = ['PO Number', 'Supplier', 'Date', 'Total Amount', 'Status'];
    const rows = filteredOrders.map(po => [
      po.poNumber,
      po.supplier || 'N/A',
      po.createdAt?.toDate ? moment(po.createdAt.toDate()).format('YYYY-MM-DD') : 'N/A',
      po.grandTotal || 0,
      po.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-orders-${moment().format('YYYY-MM-DD')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  // Pagination
  const indexOfLastOrder = currentPage * itemsPerPage;
  const indexOfFirstOrder = indexOfLastOrder - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const badges = {
      Draft: 'bg-gray-100 text-gray-800',
      Sent: 'bg-blue-100 text-blue-800',
      Acknowledged: 'bg-amber-100 text-amber-800',
      'Partially Received': 'bg-orange-100 text-orange-800',
      Completed: 'bg-emerald-100 text-emerald-800',
      Cancelled: 'bg-red-100 text-red-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-cyan-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-cyan-600 py-4">
      <BreadCrumb pages={pages} />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl">
                <ShoppingCartIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900">Purchase Orders</h1>
                <p className="text-xs text-gray-600 mt-0.5">
                  Manage and track purchase orders
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition-all duration-200"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                Export CSV
              </button>
              <Link
                to="/procurement/requests"
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
              >
                View Procurements
              </Link>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-medium text-gray-600">Total POs</p>
            <p className="text-2xl font-black text-gray-900 mt-1">{purchaseOrders.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-medium text-gray-600">Draft</p>
            <p className="text-2xl font-black text-gray-500 mt-1">
              {purchaseOrders.filter((po) => po.status === 'Draft').length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-medium text-gray-600">Sent</p>
            <p className="text-2xl font-black text-blue-600 mt-1">
              {purchaseOrders.filter((po) => po.status === 'Sent').length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-medium text-gray-600">Completed</p>
            <p className="text-2xl font-black text-emerald-600 mt-1">
              {purchaseOrders.filter((po) => po.status === 'Completed').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center space-x-2 mb-3">
            <FunnelIcon className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-bold text-gray-900">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by PO number, supplier, or procurement..."
                className="block w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Acknowledged">Acknowledged</option>
                <option value="Partially Received">Partially Received</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-500 to-cyan-600">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-white uppercase tracking-wider">
                    PO Number
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-white uppercase tracking-wider">
                    Supplier
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-white uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-2 py-1.5 text-right text-xs font-black text-white uppercase tracking-wider">
                    Total Amount
                  </th>
                  <th className="px-2 py-1.5 text-center text-xs font-black text-white uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-2 py-1.5 text-right text-xs font-black text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentOrders.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-2 py-8 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <ShoppingCartIcon className="h-12 w-12 text-gray-300 mb-3" />
                        <p className="text-sm font-medium text-gray-500">
                          {searchTerm || statusFilter
                            ? 'No purchase orders found matching your filters'
                            : 'No purchase orders yet'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentOrders.map((po) => (
                    <tr key={po.id} className="hover:bg-blue-50 transition-colors duration-150">
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <div className="text-xs font-bold text-gray-900">
                          {po.poNumber}
                        </div>
                        {po.procurementNumber && (
                          <div className="text-xs text-gray-500">
                            Req: {po.procurementNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium text-gray-900">
                        {po.supplier || 'N/A'}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium text-gray-600">
                        {po.createdAt?.toDate
                          ? moment(po.createdAt.toDate()).format('MMM DD, YYYY')
                          : 'N/A'}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-right text-xs font-black text-gray-900">
                        {formatCurrency(po.grandTotal || 0)}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-center">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-tight font-bold rounded-full ${getStatusBadge(
                            po.status
                          )}`}
                        >
                          {po.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-right">
                        <Link
                          to={`/procurement/purchase-orders/${po.id}`}
                          className="text-xs font-bold text-blue-600 hover:text-blue-900 transition-colors duration-150"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-700">
                  Showing <span className="font-bold">{indexOfFirstOrder + 1}</span> to{' '}
                  <span className="font-bold">
                    {Math.min(indexOfLastOrder, filteredOrders.length)}
                  </span>{' '}
                  of <span className="font-bold">{filteredOrders.length}</span> results
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-xs font-medium rounded-lg transition-all duration-150 ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-md'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Summary */}
        <div className="text-xs text-white text-center mt-3 font-medium">
          Showing {currentOrders.length} of {filteredOrders.length} purchase orders
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderList;
